import { useEffect, useState } from 'react';
import { formatDkk, formatNumber } from '@jywer/ui';
import { MrrLineChart } from './MrrLineChart';
import { MrrStackedBar } from './MrrStackedBar';
import { PIPELINE_STATUS_LABEL, PROJECT_TYPE_LABEL, type RevenuePipelinePayload } from './types';

const REFRESH_MS = 60_000;

export function RevenuePipelinePage() {
  const [data, setData] = useState<RevenuePipelinePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await fetch('/api/revenue-pipeline?trend_months=12');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as RevenuePipelinePayload;
        if (cancelled) return;
        setData(json);
        setLoading(false);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Kunne ikke hente pipeline');
        setLoading(false);
      }
    };
    void fetchOnce();
    const interval = setInterval(fetchOnce, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (loading) return <p className="text-sm text-ink-2">Henter pipeline…</p>;
  if (error) {
    return (
      <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const adHocTotal = Number(data.ad_hoc_pending_total);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-primary">Revenue pipeline</h1>
        <span className="text-xs text-ink-2">
          Opdateret{' '}
          {new Date(data.generated_at).toLocaleTimeString('da-DK', {
            hour: '2-digit',
            minute: '2-digit',
          })}{' '}
          · auto-refresh 60s
        </span>
      </div>

      <section className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <KpiCard label="MRR total" value={formatDkk(data.mrr_total)} />
        <KpiCard label="Nye kunder (måneden)" value={formatNumber(data.clients_new_this_month)} />
        <KpiCard label="Churned (måneden)" value={formatNumber(data.clients_churned_this_month)} />
        <KpiCard
          label="Ad-hoc pending"
          value={adHocTotal === 0 ? '—' : formatDkk(data.ad_hoc_pending_total)}
        />
      </section>

      <section className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card title="MRR fordeling">
          <MrrStackedBar rows={data.mrr_by_type} />
        </Card>
        <Card title="Unbilled project pipeline">
          <PipelineTable rows={data.pipeline} />
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-6">
        <Card title="MRR 12 måneder">
          <MrrLineChart rows={data.mrr_trend} />
        </Card>
        <Card title="Projektfase-fordeling (snapshots)">
          <PhaseTrendTable rows={data.phase_trend} />
        </Card>
      </section>
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-line-1 bg-paper-0 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-2">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-primary tabular-nums">{value}</p>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-line-1 bg-paper-0 p-4">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-2">{title}</h2>
      {children}
    </div>
  );
}

function PipelineTable({ rows }: { rows: RevenuePipelinePayload['pipeline'] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-ink-2">Ingen pending-rater lige nu.</p>;
  }
  const byType = new Map<string, typeof rows>();
  for (const r of rows) {
    const list = byType.get(r.project_type) ?? [];
    list.push(r);
    byType.set(r.project_type, list);
  }
  return (
    <div className="flex flex-col gap-4">
      {Array.from(byType.entries()).map(([t, trows]) => {
        const subtotal = trows.reduce((acc, r) => acc + Number(r.total_amount), 0);
        const count = trows.reduce((acc, r) => acc + r.rate_count, 0);
        return (
          <div key={t}>
            <div className="mb-1 flex items-baseline justify-between text-sm">
              <span className="font-medium text-primary">{PROJECT_TYPE_LABEL[t] ?? t}</span>
              <span className="text-ink-2">
                {count} rater · {formatDkk(subtotal.toString())}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-ink-2">
                  <th className="text-left font-semibold">Rate</th>
                  <th className="text-left font-semibold">Status</th>
                  <th className="text-right font-semibold">Antal</th>
                  <th className="text-right font-semibold">Beløb</th>
                </tr>
              </thead>
              <tbody>
                {trows.map((r) => (
                  <tr
                    key={`${r.project_type}-${r.rate_number}-${r.status}`}
                    className="border-t border-line-1"
                  >
                    <td>Rate {r.rate_number}</td>
                    <td>{PIPELINE_STATUS_LABEL[r.status]}</td>
                    <td className="text-right tabular-nums">{r.rate_count}</td>
                    <td className="text-right tabular-nums">{formatDkk(r.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function PhaseTrendTable({ rows }: { rows: RevenuePipelinePayload['phase_trend'] }) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-ink-2">
        Ingen phase-snapshots endnu. Månedscron kører første gang på den 22. (Europe/Copenhagen).
      </p>
    );
  }
  const byMonth = new Map<string, typeof rows>();
  for (const r of rows) {
    const list = byMonth.get(r.snapshot_month) ?? [];
    list.push(r);
    byMonth.set(r.snapshot_month, list);
  }
  const months = Array.from(byMonth.keys()).sort();
  return (
    <div className="flex flex-col gap-3">
      {months.map((month) => (
        <details key={month} className="rounded border border-line-1">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink-1 hover:bg-paper-2/40">
            {month}
          </summary>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-ink-2">
                <th className="px-3 py-1 text-left font-semibold">Type</th>
                <th className="px-3 py-1 text-left font-semibold">Fase</th>
                <th className="px-3 py-1 text-right font-semibold">Antal</th>
                <th className="px-3 py-1 text-right font-semibold">Værdi</th>
              </tr>
            </thead>
            <tbody>
              {byMonth.get(month)!.map((r) => (
                <tr
                  key={`${r.snapshot_month}-${r.project_type}-${r.phase}`}
                  className="border-t border-line-1"
                >
                  <td className="px-3 py-1">
                    {PROJECT_TYPE_LABEL[r.project_type] ?? r.project_type}
                  </td>
                  <td className="px-3 py-1">{r.phase}</td>
                  <td className="px-3 py-1 text-right tabular-nums">{r.count}</td>
                  <td className="px-3 py-1 text-right tabular-nums">{formatDkk(r.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ))}
    </div>
  );
}
